<script setup>
import FormInput from '@/components/Forms/FormInput.vue';
import { IconX } from '@tabler/icons-vue';
import { computed, ref, watch } from 'vue';
import QuestionBaseQuiz from '@/components/Quiz/QuestionBaseQuiz.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import PolynomialDisplay from '@/components/Quiz/PolynomialDisplay.vue';

const props = defineProps({
    question: {
        required: true,
    },
    modelValue: {
        required: true,
    },
});

const emit = defineEmits(['update:modelValue']);

const answer = ref(props.modelValue);
const polynomial = computed(() => {
    return {
        label: props.question.options.label,
        x_0: answer.value.x_0,
        x_1: answer.value.x_1,
        x_2: answer.value.x_2,
        x_3: answer.value.x_3,
        x_4: answer.value.x_4,
        x_5: answer.value.x_5,
        x_6: answer.value.x_6,
    };
});

function reset() {
    [0, 1, 2, 3, 4, 5, 6].forEach((i) => {
        answer.value[`x_${i}`] = null;
    });
}

const isEmpty = computed(() => {
    return !(
        answer.value.x_0 ||
        answer.value.x_1 ||
        answer.value.x_2 ||
        answer.value.x_3 ||
        answer.value.x_4 ||
        answer.value.x_5 ||
        answer.value.x_6
    );
});

const stringifiedAnswer = computed(() => {
    if (isEmpty.value) {
        return null;
    } else {
        return JSON.stringify(answer.value);
    }
});

watch(
    () => stringifiedAnswer.value,
    (newVal) => {
        emit('update:modelValue', newVal);
    },
    { immediate: true },
);
</script>

<template>
    <QuestionBaseQuiz :question="question">
        <div class="flex gap-2 flex-wrap mt-1">
            <div class="flex gap-1 p-1 rounded bg-gray-50 items-center" v-for="i in question.options.range" :key="i">
                <FormInput
                    :id="`x_${i}`"
                    v-model.trim="answer[`x_${i}`]"
                    :name="`x_${i}`"
                    @keypress.enter.prevent
                    @wheel.passive="$event.target.blur()"
                    type="number"
                    step="1"
                    class="mt-1 w-20 text-right"
                />
                <FormLabel :for="`x_${i}`">
                    <span v-if="i === 0">
                        {{ $t('messages.polynomial_degree_zero_term') }}
                    </span>
                    <span v-else>
                        {{ question.options.label }} <sup v-if="i > 1">{{ i }}</sup>
                    </span>
                </FormLabel>
            </div>
        </div>

        <div class="pt-1 text-sm flex flex-wrap items-center gap-2" :class="{ invisible: isEmpty }">
            <div>{{ $t('messages.your_answer') }}:</div>
            <div class="bg-gray-100 p-2 h-8">
                <PolynomialDisplay :polynomial="polynomial" />
            </div>
        </div>

        <div class="mt-1">
            <button @click="reset" :class="{ invisible: isEmpty }" class="text-gray-600" type="button">
                <IconX class="w-6 h-6 inline-block" />
                {{ $t('messages.delete_answer') }}
            </button>
        </div>
    </QuestionBaseQuiz>
</template>

<script>
export default {
    name: 'QuestionMathPolynomialOperationQuiz',
};
</script>
